import { ArrowDown, ArrowLeft, ArrowUp, Copy, Trash2 } from "lucide-react";
import PropTypes from "prop-types";
import { Input, TextArea } from "../../../components/FormComponents";
import SectionEditor from "../../../components/SectionEditor";
import SectionToolbar from "../../../components/SectionToolbar";

export default function CanvasEditorPane({
	activeSectionIndex,
	setActiveSectionIndex,
	sections,
	formData,
	setFormData,
	addSection,
	updateSection,
	removeSection,
	moveSection,
	duplicateSection,
	showNotification,
}) {
	if (activeSectionIndex !== null && sections[activeSectionIndex]) {
		return (
			<div className="space-y-4 animate-in slide-in-from-left-4 duration-200">
				<div className="flex items-center gap-2 mb-4">
					<button
						type="button"
						onClick={() => setActiveSectionIndex(null)}
						className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition"
						aria-label="Voltar para visão geral"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<span className="text-sm font-bold text-white uppercase tracking-wider">
						Editar Seção
					</span>
				</div>

				<div className="bg-slate-800/50 p-4 rounded-xl border border-white/5 space-y-4">
					<div className="flex items-center justify-between border-b border-white/5 pb-3">
						<span className="text-xs text-cyan-400 font-bold uppercase">
							{sections[activeSectionIndex].type}
						</span>
						<div className="flex gap-1">
							<button
								type="button"
								onClick={() =>
									moveSection(activeSectionIndex, activeSectionIndex - 1)
								}
								disabled={activeSectionIndex === 0}
								className="p-1.5 hover:bg-slate-700 rounded text-slate-400 disabled:opacity-30"
								aria-label="Mover para cima"
							>
								<ArrowUp className="w-4 h-4" />
							</button>
							<button
								type="button"
								onClick={() =>
									moveSection(activeSectionIndex, activeSectionIndex + 1)
								}
								disabled={activeSectionIndex === sections.length - 1}
								className="p-1.5 hover:bg-slate-700 rounded text-slate-400 disabled:opacity-30"
								aria-label="Mover para baixo"
							>
								<ArrowDown className="w-4 h-4" />
							</button>
							<button
								type="button"
								onClick={() => duplicateSection(activeSectionIndex)}
								className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
								aria-label="Duplicar seção"
							>
								<Copy className="w-4 h-4" />
							</button>
							<button
								type="button"
								onClick={() => {
									removeSection(activeSectionIndex);
									setActiveSectionIndex(null);
								}}
								className="p-1.5 hover:bg-red-900/30 rounded text-slate-400 hover:text-red-400"
								aria-label="Remover seção"
							>
								<Trash2 className="w-4 h-4" />
							</button>
						</div>
					</div>

					<SectionEditor
						section={sections[activeSectionIndex]}
						index={activeSectionIndex}
						onChange={(field, value) =>
							updateSection(activeSectionIndex, field, value)
						}
						onRemove={() => removeSection(activeSectionIndex)}
						collapsed={false} // Always expanded in contextual mode
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 animate-in slide-in-from-left-4 duration-200">
			<div className="space-y-4">
				<Input
					label="Titulo da Proposta"
					value={formData.title}
					onChange={(e) => setFormData({ ...formData, title: e.target.value })}
					placeholder="Ex: Proposta de Implantacao do Sistema"
				/>
				<TextArea
					label="Descricao"
					value={formData.description}
					onChange={(e) =>
						setFormData({ ...formData, description: e.target.value })
					}
					placeholder="Descreva brevemente a proposta..."
					rows="3"
				/>
				<Input
					label="Validade (dias)"
					type="number"
					value={formData.validity_days}
					onChange={(e) =>
						setFormData({
							...formData,
							validity_days: parseInt(e.target.value, 10) || 30,
						})
					}
				/>
			</div>

			<div className="border-t border-slate-700 pt-4">
				<h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
					Estrutura & Seções
				</h3>
				<SectionToolbar
					sections={sections}
					onAdd={(sec) => {
						addSection(sec);
						// Auto-select the newly added section (last one)
						setTimeout(() => setActiveSectionIndex(sections.length), 0);
					}}
					onNotify={showNotification}
				/>
				<div className="mt-4 space-y-2">
					{sections.map((sec, idx) => (
						<button
							key={sec.id || `section-list-${idx}`}
							type="button"
							onClick={() => setActiveSectionIndex(idx)}
							className={`w-full text-left rounded-lg border p-2 cursor-pointer transition-all focus:outline-none focus:ring-1 focus:ring-cyan-500/50 ${
								activeSectionIndex === idx
									? "bg-cyan-900/20 border-cyan-500/50"
									: "bg-slate-800/30 border-white/5 hover:border-white/20"
							}`}
						>
							<div className="flex items-center justify-between">
								<span className="text-[10px] font-bold text-slate-500 uppercase">
									Seção {idx + 1}: {sec.type}
								</span>
								<span className="text-[10px] text-slate-600 truncate max-w-[100px]">
									{sec.title || "Sem título"}
								</span>
							</div>
						</button>
					))}
					{sections.length === 0 && (
						<p className="text-xs text-slate-600 text-center py-4">
							Nenhuma seção adicionada
						</p>
					)}
				</div>
			</div>

			<TextArea
				label="Notas internas"
				value={formData.notes}
				onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
				placeholder="Observacoes internas..."
				rows="2"
			/>
		</div>
	);
}

CanvasEditorPane.propTypes = {
	activeSectionIndex: PropTypes.number,
	setActiveSectionIndex: PropTypes.func.isRequired,
	sections: PropTypes.array.isRequired,
	formData: PropTypes.object.isRequired,
	setFormData: PropTypes.func.isRequired,
	addSection: PropTypes.func.isRequired,
	updateSection: PropTypes.func.isRequired,
	removeSection: PropTypes.func.isRequired,
	moveSection: PropTypes.func.isRequired,
	duplicateSection: PropTypes.func.isRequired,
	showNotification: PropTypes.func.isRequired,
};
